import React, { useState, useEffect, useCallback } from 'react'
import styles from '../AdminDashboard.module.scss'
import ConfirmDialog from './ConfirmDialog'
import { config } from '../../../config'

interface Queue {
  name: string
  vhost: string
  messages: number
  messages_ready: number
  messages_unacked: number
  consumers: number
  state: string
  memory: number
  message_bytes: number
}

interface QueuesResponse {
  success: boolean
  queues: Queue[]
  total: number
  total_messages: number
}

const QueueManager: React.FC = () => {
  const [queues, setQueues] = useState<Queue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalMessages, setTotalMessages] = useState(0)
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    queueName: string | null
    isPurgeAll: boolean
  }>({
    isOpen: false,
    title: '',
    message: '',
    queueName: null,
    isPurgeAll: false,
  })
  const [actionLoading, setActionLoading] = useState(false)

  const fetchQueues = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${config.API_URL}/admin/queues`)
      if (!response.ok) throw new Error('Failed to fetch queues')
      const data: QueuesResponse = await response.json()
      setQueues(data.queues)
      setTotalMessages(data.total_messages)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchQueues()
  }, [fetchQueues])

  const handlePurgeClick = (queueName: string, messageCount: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Purge Queue',
      message: `Are you sure you want to purge "${queueName}"? This will delete ${messageCount.toLocaleString()} message(s).`,
      queueName,
      isPurgeAll: false,
    })
  }

  const handlePurgeAllClick = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Purge All Queues',
      message: `Are you sure you want to purge ALL queues? This will delete ${totalMessages.toLocaleString()} message(s) across ${
        queues.length
      } queue(s). This action cannot be undone!`,
      queueName: null,
      isPurgeAll: true,
    })
  }

  const handleConfirm = async () => {
    setActionLoading(true)
    try {
      let url: string
      if (confirmDialog.isPurgeAll) {
        url = `${config.API_URL}/admin/queues/purge-all`
      } else {
        url = `${config.API_URL}/admin/queues/${encodeURIComponent(confirmDialog.queueName!)}/purge`
      }

      const response = await fetch(url, { method: 'POST' })
      if (!response.ok) throw new Error('Purge operation failed')

      await fetchQueues()
      setConfirmDialog({ ...confirmDialog, isOpen: false })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Purge failed')
    } finally {
      setActionLoading(false)
    }
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  const getStateColor = (state: string): string => {
    switch (state) {
      case 'running':
        return styles.stateRunning
      case 'idle':
        return styles.stateIdle
      default:
        return styles.stateUnknown
    }
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <span>Loading queues...</span>
      </div>
    )
  }

  return (
    <div className={styles.managerContainer}>
      <div className={styles.managerHeader}>
        <div className={styles.headerLeft}>
          <h2>Queue Management</h2>
          <span className={styles.badge}>
            {queues.length} queues · {totalMessages.toLocaleString()} messages
          </span>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.refreshButton} onClick={fetchQueues}>
            ↻ Refresh
          </button>
          <button className={styles.dangerButton} onClick={handlePurgeAllClick} disabled={totalMessages === 0}>
            Purge All Queues
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Queue Name</th>
              <th>Messages</th>
              <th>Ready</th>
              <th>Unacked</th>
              <th>Consumers</th>
              <th>Memory</th>
              <th>State</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {queues.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.emptyRow}>
                  No queues found
                </td>
              </tr>
            ) : (
              queues.map((queue) => (
                <tr key={queue.name}>
                  <td className={styles.queueName}>
                    <code>{queue.name}</code>
                  </td>
                  <td className={queue.messages > 0 ? styles.highlightCell : ''}>{queue.messages.toLocaleString()}</td>
                  <td>{queue.messages_ready.toLocaleString()}</td>
                  <td>{queue.messages_unacked.toLocaleString()}</td>
                  <td>{queue.consumers}</td>
                  <td>{formatBytes(queue.memory)}</td>
                  <td>
                    <span className={`${styles.stateBadge} ${getStateColor(queue.state)}`}>{queue.state}</span>
                  </td>
                  <td>
                    <button
                      className={styles.purgeButton}
                      onClick={() => handlePurgeClick(queue.name, queue.messages)}
                      disabled={queue.messages === 0}
                      title={queue.messages === 0 ? 'Queue is empty' : `Purge ${queue.messages} messages`}>
                      Purge
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.isPurgeAll ? 'Purge All' : 'Purge'}
        isDanger={true}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        isLoading={actionLoading}
      />
    </div>
  )
}

export default QueueManager
